import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Mail, Phone, Send, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "GST Services",
  "Income Tax Services",
  "Audit Services",
  "Legal & Litigation",
  "Business Registration",
  "Compliance Services",
  "Professional Advisory",
  "Other",
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) => {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.service || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Inquiry submitted successfully! We'll get back to you soon.");
      setSent(true);
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch {
      toast.error("Failed to submit inquiry. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 bg-[#131821] relative overflow-hidden"
    >
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#D4AF37]/[0.02] rounded-full blur-3xl" />

      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <div className="text-center mb-16">
          <p className="font-work text-sm font-semibold tracking-[0.2em] text-[#D4AF37] uppercase mb-4">
            Get in Touch
          </p>
          <h2 className="font-outfit text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-white">
            Schedule a Consultation
          </h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Form */}
          <div className="lg:col-span-3">
            {sent ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                data-testid="contact-success"
                className="bg-[#0A0D14] border border-[#D4AF37]/20 p-12 text-center"
              >
                <CheckCircle size={48} className="text-[#D4AF37] mx-auto mb-4" />
                <h3 className="font-outfit text-2xl text-white mb-2">Thank You!</h3>
                <p className="font-work text-[#94A3B8]">
                  We've received your inquiry. Our team will contact you shortly.
                </p>
                <button
                  onClick={() => setSent(false)}
                  data-testid="send-another-btn"
                  className="mt-6 border border-[#D4AF37] text-[#D4AF37] px-6 py-2 text-sm font-work hover:bg-[#D4AF37] hover:text-[#0A0D14] transition-colors"
                >
                  Send Another Inquiry
                </button>
              </motion.div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="font-work text-xs text-[#94A3B8] uppercase tracking-wider mb-2 block">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      data-testid="contact-name"
                      placeholder="Your full name"
                      className="w-full bg-[#0A0D14] border border-white/10 px-4 py-3 text-white font-work text-sm focus:outline-none focus:border-[#D4AF37] transition-colors placeholder:text-[#94A3B8]/40"
                    />
                  </div>
                  <div>
                    <label className="font-work text-xs text-[#94A3B8] uppercase tracking-wider mb-2 block">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      data-testid="contact-email"
                      placeholder="your@email.com"
                      className="w-full bg-[#0A0D14] border border-white/10 px-4 py-3 text-white font-work text-sm focus:outline-none focus:border-[#D4AF37] transition-colors placeholder:text-[#94A3B8]/40"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="font-work text-xs text-[#94A3B8] uppercase tracking-wider mb-2 block">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      data-testid="contact-phone"
                      placeholder="+91 XXXXXXXXXX"
                      className="w-full bg-[#0A0D14] border border-white/10 px-4 py-3 text-white font-work text-sm focus:outline-none focus:border-[#D4AF37] transition-colors placeholder:text-[#94A3B8]/40"
                    />
                  </div>
                  <div>
                    <label className="font-work text-xs text-[#94A3B8] uppercase tracking-wider mb-2 block">
                      Service Required *
                    </label>
                    <select
                      name="service"
                      value={form.service}
                      onChange={handleChange}
                      data-testid="contact-service"
                      className="w-full bg-[#0A0D14] border border-white/10 px-4 py-3 text-white font-work text-sm focus:outline-none focus:border-[#D4AF37] transition-colors appearance-none"
                    >
                      <option value="" className="bg-[#0A0D14]">Select a service</option>
                      {SERVICE_OPTIONS.map((s) => (
                        <option key={s} value={s} className="bg-[#0A0D14]">{s}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="font-work text-xs text-[#94A3B8] uppercase tracking-wider mb-2 block">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows={5}
                    data-testid="contact-message"
                    placeholder="Tell us about your requirement..."
                    className="w-full bg-[#0A0D14] border border-white/10 px-4 py-3 text-white font-work text-sm focus:outline-none focus:border-[#D4AF37] transition-colors resize-none placeholder:text-[#94A3B8]/40"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit-btn"
                  className="bg-[#D4AF37] text-[#0A0D14] font-work font-medium px-8 py-4 hover:bg-[#F4D068] transition-colors inline-flex items-center gap-2 disabled:opacity-50"
                >
                  {loading ? "Submitting..." : "Submit Inquiry"}
                  <Send size={16} />
                </button>
              </form>
            )}
          </div>

          {/* Contact Info */}
          <div className="lg:col-span-2 space-y-8">
            <div data-testid="contact-info-address">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-[#D4AF37]/10 flex items-center justify-center">
                  <MapPin size={18} className="text-[#D4AF37]" />
                </div>
                <h4 className="font-outfit text-white font-medium">Our Office</h4>
              </div>
              <p className="font-work text-sm text-[#94A3B8] leading-relaxed pl-[52px]">
                Office No-120, First Floor,<br />
                Gagandeep Building, Rajendra Place,<br />
                New Delhi - 110008<br />
                <span className="text-[#D4AF37]/60 text-xs">
                  Nearest Metro: Rajendra Place
                </span>
              </p>
            </div>

            <div data-testid="contact-info-email">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-[#D4AF37]/10 flex items-center justify-center">
                  <Mail size={18} className="text-[#D4AF37]" />
                </div>
                <h4 className="font-outfit text-white font-medium">Email</h4>
              </div>
              <a
                href="mailto:avs_associates@yahoo.com"
                className="font-work text-sm text-[#94A3B8] hover:text-[#D4AF37] transition-colors pl-[52px] block"
              >
                avs_associates@yahoo.com
              </a>
            </div>

            <div data-testid="contact-info-phone">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-[#D4AF37]/10 flex items-center justify-center">
                  <Phone size={18} className="text-[#D4AF37]" />
                </div>
                <h4 className="font-outfit text-white font-medium">Phone</h4>
              </div>
              <a
                href="tel:+919990072390"
                className="font-work text-sm text-[#94A3B8] hover:text-[#D4AF37] transition-colors pl-[52px] block"
              >
                +91 9990072390
              </a>
            </div>

            {/* Map placeholder */}
            <div className="bg-[#0A0D14] border border-white/5 p-6 mt-8">
              <p className="font-work text-xs text-[#94A3B8] uppercase tracking-wider mb-3">
                Location
              </p>
              <div className="aspect-[4/3] bg-[#1A202C] flex items-center justify-center">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3501.5!2d77.1728!3d28.6381!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390d0341b1b1b1b1%3A0x1234567890!2sRajendra+Place%2C+New+Delhi!5e0!3m2!1sen!2sin!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0, filter: "invert(90%) hue-rotate(180deg) brightness(0.9)" }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Office Location"
                  data-testid="contact-map"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
